/**
 * useBulkActions Hook
 * Phase 1 Day 7 - Bulk lockdown action management
 *
 * Manages bulk lockdown operations across multiple devices in an installation.
 * Handles device eligibility filtering, action execution, status tracking,
 * and result management for bulk operations.
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.installationId - Current installation ID
 * @param {Array} params.devices - Array of devices
 * @param {Object} params.deviceStates - Map of device states
 * @returns {Object} Bulk action handlers and state management
 */

import { useCallback } from "react";
import config from "../config";
import logger from "../utils/logger";
import { useNotification } from "./useNotification";
import { useDeviceActions } from "./useDeviceActions";
import { useBulkOperationsStore, useUIStore } from "../stores";

export const useBulkActions = ({
  installationId,
  devices,
  deviceStates,
  authenticatedFetch,
  invalidateDeviceStateCache,
}) => {
  // Get notification functions
  const { showNotification } = useNotification();

  // Get device actions (for bulk lockdown)
  const { executeBulkLockdownAction } = useDeviceActions(
    authenticatedFetch,
    invalidateDeviceStateCache
  );

  // Get bulk action state management from UI store
  const setBulkActionResults = useUIStore(
    (state) => state.setBulkActionResults
  );
  const setBulkActionType = useUIStore((state) => state.setBulkActionType);
  const setBulkActionStatuses = useUIStore(
    (state) => state.setBulkActionStatuses
  );

  // Get bulk device lockdown states from bulk operations store
  const setBulkDeviceLockdownStates = useBulkOperationsStore(
    (state) => state.setBulkDeviceLockdownStates
  );

  // Get UI state management
  const setBulkStatusModalOpen = useUIStore(
    (state) => state.setBulkStatusModalOpen
  );

  /**
   * Execute bulk lockdown action across selected devices
   * @param {string} actionType - Action type (LOCK_DOWN, FREEZE, etc.)
   * @param {Array} selectedDeviceIds - Array of selected device IDs
   */
  const handleBulkLockdownAction = useCallback(
    async (actionType, selectedDeviceIds = []) => {
      logger.log(
        `[BULK ACTION] 🚨 Executing BULK ${actionType} for installation ${installationId}...`
      );
      logger.log(
        `[BULK ACTION] 📍 Endpoint: ${config.apiEndpoint}/bulk-lockdown`
      );
      logger.log(
        `[BULK ACTION] 📝 Received ${selectedDeviceIds.length} selected device IDs:`,
        selectedDeviceIds
      );

      // Deduplicate selected device IDs
      const uniqueSelectedIds = [...new Set(selectedDeviceIds)];
      if (uniqueSelectedIds.length !== selectedDeviceIds.length) {
        logger.warn(
          `[BULK ACTION] ⚠️ Found ${
            selectedDeviceIds.length - uniqueSelectedIds.length
          } duplicate ID(s) in selection, deduplicated to ${
            uniqueSelectedIds.length
          }`
        );
      }
      logger.log(
        `[BULK ACTION] 📝 Using ${uniqueSelectedIds.length} unique device IDs:`,
        uniqueSelectedIds
      );

      // DEBUG: Log current deviceStates to diagnose filtering issue
      logger.log(`[BulkAction] 🔍 Current deviceStates:`, deviceStates);
      logger.log(`[BulkAction] 🔍 Total devices:`, devices.length);
      logger.log(`[BulkAction] 🔍 Complete devices array:`, devices);
      devices.forEach((device) => {
        const state = deviceStates[device.deviceId];
        logger.log(
          `[BulkAction] 🔍 Device ${device.deviceId} (${
            device.displayName
          }): lockdownState="${
            state?.lockdownState
          }", stateExists=${!!state}, hasLockdownState=${!!state?.lockdownState}`
        );
      });

      logger.log(
        `[BulkAction] 🎯 Using ${uniqueSelectedIds.length} unique selected device(s)`
      );

      // Validate that selected devices are still eligible and exist
      const selectedDevices = devices.filter((device) =>
        uniqueSelectedIds.includes(device.deviceId)
      );

      if (selectedDevices.length === 0) {
        showNotification({
          message: "No valid devices selected for this action.",
          type: "error",
        });
        return;
      }

      // Verify eligibility of selected devices
      const eligibleDevices = selectedDevices.filter((device) => {
        const deviceState = deviceStates[device.deviceId];

        // If we have no state at all for this device, exclude it —
        // we genuinely don't know anything about it.
        if (!deviceState) {
          logger.log(
            `[BulkAction] Excluding device ${device.deviceId} - no deviceState entry`
          );
          return false;
        }

        // Treat missing lockdownState as "Lockdown Off" (default state) —
        // consistent with useEligibleDevices.js which counts these as eligible.
        const lockdownState = deviceState.lockdownState || "Lockdown Off";

        if (actionType === "LOCK_DOWN") {
          const isEligible = lockdownState !== "Lockdown On";
          if (!isEligible) {
            logger.log(
              `[LOCK_DOWN] Excluding device ${device.deviceId} - already in lockdown`
            );
          }
          return isEligible;
        } else if (actionType === "FREEZE") {
          const isEligible = lockdownState === "Lockdown On";
          if (!isEligible) {
            logger.log(
              `[FREEZE] Excluding device ${device.deviceId} - not in lockdown`
            );
          }
          return isEligible;
        }
        return false;
      });

      if (eligibleDevices.length === 0) {
        showNotification({
          message:
            actionType === "LOCK_DOWN"
              ? "Selected devices are already in lockdown state."
              : "Selected devices are not in lockdown state.",
          type: "error",
        });
        return;
      }

      logger.log(
        `[BulkAction] ✅ Validated ${eligibleDevices.length} eligible devices from ${selectedDevices.length} selected`
      );
      logger.log(
        `[BulkAction] 📋 Eligible device IDs:`,
        eligibleDevices.map((d) => d.deviceId)
      );

      try {
        // Extract device IDs from eligible devices
        const deviceIds = eligibleDevices.map((device) => device.deviceId);
        logger.log(
          `[BulkAction] 📤 CRITICAL: About to send ${deviceIds.length} device IDs to backend`
        );
        logger.log(`[BulkAction] 📤 Device IDs array:`, deviceIds);

        const result = await executeBulkLockdownAction(
          installationId,
          actionType,
          deviceIds
        );

        logger.log("[BulkAction] 📥 Backend response:", result);
        logger.log(
          `[BulkAction] 📥 Backend processed ${
            result.results?.length || 0
          } devices`
        );

        // Filter action results to only include eligible devices
        // This is necessary because the backend may return extra devices that we didn't request
        let actionResults = result.results || [];
        logger.log(
          `[BulkAction] 🔍 BEFORE filtering: ${actionResults.length} results`
        );
        logger.log(
          `[BulkAction] 🔍 Result device IDs BEFORE filter:`,
          actionResults.map((r) => r.deviceId)
        );

        // Filter results to match only the devices we intended to act on
        const eligibleDeviceIds = new Set(
          eligibleDevices.map((d) => d.deviceId)
        );
        logger.log(
          `[${actionType}] 🎯 Eligible device IDs for filtering:`,
          Array.from(eligibleDeviceIds)
        );

        const unfilteredCount = actionResults.length;
        actionResults = actionResults.filter((r) =>
          eligibleDeviceIds.has(r.deviceId)
        );

        logger.log(
          `[${actionType}] ✂️ AFTER filtering: ${actionResults.length} results (filtered from ${unfilteredCount})`
        );
        logger.log(
          `[${actionType}] ✂️ Result device IDs AFTER filter:`,
          actionResults.map((r) => r.deviceId)
        );

        logger.log(
          `[BulkAction] 💾 Setting bulk action results with ${actionResults.length} devices`
        );

        setBulkActionResults(actionResults);
        setBulkActionType(actionType);

        // Initialize statuses as PENDING for all actions
        // BUT: preserve any statuses that were already set by early-arriving lastActionUpdated events
        setBulkActionStatuses((prevStatuses) => {
          const initialStatuses = {};
          actionResults.forEach((result) => {
            if (result.actionId) {
              // If we already have a status from an early event, keep it; otherwise set to PENDING
              initialStatuses[result.actionId] =
                prevStatuses[result.actionId] || "PENDING";
            }
          });

          logger.log(
            `[BulkAction] Initializing ${
              Object.keys(initialStatuses).length
            } action statuses (preserving ${
              Object.keys(prevStatuses).length
            } existing):`,
            initialStatuses
          );

          return initialStatuses;
        });

        // Initialize device lock state tracking
        // Set all devices to PENDING, waiting for lockdownState confirmation
        const initialDeviceLockdownStates = {};
        actionResults.forEach((result) => {
          if (result.deviceId) {
            initialDeviceLockdownStates[result.deviceId] = "PENDING";
          }
        });
        setBulkDeviceLockdownStates(initialDeviceLockdownStates);

        // Open the status modal
        setBulkStatusModalOpen(true);

        const actionLabel =
          actionType === "LOCK_DOWN" ? "Lockdown" : "Release Lockdown";
        const deviceCountMsg =
          actionType === "FREEZE"
            ? `${actionResults.length} locked device${
                actionResults.length !== 1 ? "s" : ""
              }`
            : `${actionResults.length} device${
                actionResults.length !== 1 ? "s" : ""
              }`;

        showNotification({
          message: `${actionLabel} initiated for ${deviceCountMsg}.`,
          type: "success",
        });
      } catch (error) {
        logger.error("Failed to execute bulk lockdown action:", error);
        showNotification({
          message: `Bulk action failed: ${error.message}`,
          type: "error",
        });
      }
    },
    [
      installationId,
      devices,
      deviceStates,
      showNotification,
      executeBulkLockdownAction,
      setBulkDeviceLockdownStates,
      setBulkStatusModalOpen,
      setBulkActionType,
      setBulkActionResults,
      setBulkActionStatuses,
    ]
  );

  return {
    // Actions
    handleBulkLockdownAction,
  };
};
